import { useCallback } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useSocket, useSocketChannel } from '@frontend/hooks/useSocket';

import { agentExecutionsService, type UnifiedExecutionsParams } from '../services/agentExecutions';

// The backend emits one socket event per execution status change. A bulk op (e.g.
// select-all bulk-cancel of up to 500) emits a burst, then the transitions trickle in
// ~1/sec as they drain. invalidateQueries defaults to cancelRefetch:true, so refetching
// per event aborts the in-flight list fetch on every message — a request storm.
// Throttle instead: refetch on the leading edge (snappy), coalesce events within
// SOCKET_REFETCH_WAIT_MS, and cap a sustained stream to one refetch per
// SOCKET_REFETCH_MAX_WAIT_MS (matching the disconnected-poll cadence). `invalidate`
// must be useCallback-stable so the subscription doesn't churn each render.
const SOCKET_REFETCH_WAIT_MS = 2000;
const SOCKET_REFETCH_MAX_WAIT_MS = 5000;

function useExecutionsSocketRefetch(instanceId: string | undefined, invalidate: () => void) {
  const throttledInvalidate = useDebounceCallback(invalidate, SOCKET_REFETCH_WAIT_MS, {
    leading: true,
    maxWait: SOCKET_REFETCH_MAX_WAIT_MS,
  });
  useSocketChannel(instanceId ? `agent_executions:${instanceId}` : null, throttledInvalidate);
}

export const useUnifiedExecutions = (params: UnifiedExecutionsParams) => {
  const { connected: socketConnected } = useSocket();
  const queryClient = useQueryClient();

  // Socket-driven refetch (debounced to survive bulk-operation event bursts)
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['unifiedExecutions', params.instanceId] });
  }, [queryClient, params.instanceId]);
  useExecutionsSocketRefetch(params.instanceId, invalidate);

  return useQuery({
    queryKey: [
      'unifiedExecutions',
      params.instanceId,
      params.page,
      params.pageSize,
      params.agentType,
      params.status,
      params.searchQuery,
      params.userId,
      params.agentId,
      params.dateFrom,
      params.dateTo,
    ],
    queryFn: () => agentExecutionsService.getUnifiedExecutions(params),
    enabled: !!params.instanceId,
    refetchInterval: !socketConnected ? 5000 : false,
    retry: 1,
  });
};

export const useCancellableExecutionIds = (
  params: Omit<UnifiedExecutionsParams, 'page' | 'pageSize' | 'status'>,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [
      'cancellableExecutionIds',
      params.instanceId,
      params.agentType,
      params.searchQuery,
      params.userId,
      params.agentId,
      params.dateFrom,
      params.dateTo,
    ],
    queryFn: () => agentExecutionsService.getCancellableExecutionIds(params),
    enabled: !!params.instanceId && (options?.enabled ?? true),
  });
};

export const useAgentExecutions = ({
  instanceId,
  agentId,
  page = 1,
  pageSize = 100,
  status,
  searchQuery,
}: {
  instanceId: string;
  agentId?: string;
  page?: number;
  pageSize?: number;
  status?: string;
  searchQuery?: string;
}) => {
  const { connected: socketConnected } = useSocket();
  const queryClient = useQueryClient();

  // Socket-driven refetch (debounced to survive bulk-operation event bursts)
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['agentExecutions', instanceId] });
    if (agentId) {
      queryClient.invalidateQueries({
        queryKey: ['specializedAgentExecutions', instanceId, agentId],
      });
    }
  }, [queryClient, instanceId, agentId]);
  useExecutionsSocketRefetch(instanceId, invalidate);

  return useQuery({
    queryKey: ['agentExecutions', instanceId, agentId, page, pageSize, status, searchQuery],
    queryFn: () => {
      return agentExecutionsService.getExecutions({
        instanceId,
        agentId,
        page,
        pageSize,
        status,
        searchQuery,
      });
    },
    enabled: !!instanceId,
    refetchInterval: !socketConnected ? 5000 : false,
    retry: 1,
  });
};

export const useExecutionDetails = (executionUrl?: string, shouldPoll = false) => {
  return useQuery({
    queryKey: ['executionDetails', executionUrl],
    queryFn: () => {
      if (!executionUrl) throw new Error('executionUrl is required');
      return agentExecutionsService.getExecutionDetails(executionUrl);
    },
    enabled: !!executionUrl,
    staleTime: shouldPoll ? 0 : 1000 * 60, // No caching when polling, otherwise cache for 1 minute
    refetchInterval: shouldPoll ? 5000 : false, // Poll every 5 seconds when shouldPoll is true
    retry: 1,
  });
};

export const useMemoryDetails = (memoryUrl?: string, shouldPoll = false) => {
  return useQuery({
    queryKey: ['memoryDetails', memoryUrl],
    queryFn: () => {
      if (!memoryUrl) throw new Error('memoryUrl is required');
      return agentExecutionsService.getMemoryDetails(memoryUrl);
    },
    enabled: !!memoryUrl,
    staleTime: shouldPoll ? 0 : 1000 * 60, // No caching when polling, otherwise cache for 1 minute
    refetchInterval: shouldPoll ? 5000 : false, // Poll every 5 seconds when shouldPoll is true
    retry: 1,
  });
};

export const useMemoryChunks = (chunksUrl?: string, shouldPoll = false) => {
  return useQuery({
    queryKey: ['memoryChunks', chunksUrl],
    queryFn: () => {
      if (!chunksUrl) throw new Error('chunksUrl is required');
      return agentExecutionsService.getMemoryChunks(chunksUrl);
    },
    enabled: !!chunksUrl,
    staleTime: shouldPoll ? 0 : 1000 * 60, // No caching when polling, otherwise cache for 1 minute
    refetchInterval: shouldPoll ? 5000 : false, // Poll every 5 seconds when shouldPoll is true
    retry: 1,
  });
};

// Lightweight count of an agent's executions over an optional date range.
// Drives the export dialog's live banner (>500 ZIP, >10k disable).
export const useExecutionRangeCount = (
  params: {
    instanceId: string;
    agentType: 'specialized' | 'workflow';
    agentId: string;
    startDate?: string;
    endDate?: string;
  },
  enabled: boolean
) => {
  return useQuery({
    queryKey: [
      'executionRangeCount',
      params.instanceId,
      params.agentType,
      params.agentId,
      params.startDate,
      params.endDate,
    ],
    queryFn: () => agentExecutionsService.getExecutionRangeCount(params),
    enabled: enabled && !!params.instanceId && !!params.agentId,
    staleTime: 1000 * 30,
    retry: 1,
  });
};

// Hook specifically for specialized agent executions
export const useSpecializedAgentExecutions = (
  instanceId: string,
  agentId: string,
  page = 1,
  pageSize = 20,
  agentVersion?: number | null
) => {
  const { connected: socketConnected } = useSocket();
  const queryClient = useQueryClient();

  // Socket-driven refetch (debounced to survive bulk-operation event bursts)
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['specializedAgentExecutions', instanceId, agentId],
    });
    queryClient.invalidateQueries({ queryKey: ['agentExecutions', instanceId] });
  }, [queryClient, instanceId, agentId]);
  useExecutionsSocketRefetch(instanceId, invalidate);

  return useQuery({
    queryKey: ['specializedAgentExecutions', instanceId, agentId, page, pageSize, agentVersion],
    queryFn: () =>
      agentExecutionsService.getAgentExecutions(instanceId, agentId, page, pageSize, agentVersion),
    enabled: !!instanceId && !!agentId,
    staleTime: 1000 * 60, // Cache for 1 minute
    refetchInterval: !socketConnected ? 5000 : false,
    retry: 1,
  });
};
